import { getAPI, uploadCSVAPI, downloadCSV } from "./Networking";
import dayjs from "dayjs";
import { SalesAnalyticsSelectedFilters } from "../models/Dashboard/SalesDashboard/SalesAnalyticsFilters";
import {
  DailyIncentive,
  PipelineStage,
  SalesAnalyticsData,
} from "../models/Dashboard/SalesDashboard/SalesAnalytics";
import { FilterValue } from "../models/Filters/FilterValue";
import { PaginatedAPIResponse } from "../models/NetworkResponse";
import { PipelineData } from "../models/Dashboard/SalesDashboard/SalesInterfaces";
import { APIResponse, APIBlobResponse } from "../models/NetworkResponse";

export default class SalesAnalyticsDashboardAPI {
  getSalesAnalyticsData = async (
    filters: SalesAnalyticsSelectedFilters,
  ): Promise<SalesAnalyticsData | null> => {
    try {
      const start_date = dayjs(filters.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const end_date = dayjs(filters.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const date = filters.end_date;

      const queryParams = new URLSearchParams();

      if (start_date) queryParams.append("start_date", start_date);

      if (end_date) queryParams.append("end_date", end_date);

      if (date) {
        queryParams.append("date", date);
      }

      if (filters.owner_ids && filters.owner_ids.length > 0) {
        queryParams.append(
          "owner_ids",
          filters.owner_ids.map((item) => item.id).join(","),
        );
      }

      if (filters.team_ids && filters.team_ids.length > 0) {
        queryParams.append(
          "team_ids",
          filters.team_ids.map((item) => item.id).join(","),
        );
      }

      const response = await getAPI(
        `/v5/sales-analytics?${queryParams.toString()}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOwners = async (
    searchText?: string,
  ): Promise<PaginatedAPIResponse<FilterValue>> => {
    try {
      let filters = {};
      if (searchText && searchText.length > 2) {
        filters = {
          search_text: searchText || "",
        };
      }
      const queryString = new URLSearchParams(filters).toString();
      const response = await getAPI(
        `/v5/sales-analytics/owners?${queryString}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getLeadsPipeline = async (
    filters: SalesAnalyticsSelectedFilters,
    stage: number,
  ): Promise<PipelineData> => {
    try {
      const start_date = dayjs(filters.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const end_date = dayjs(filters.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const queryParams = new URLSearchParams();
      if (stage === PipelineStage.CLOSED) {
        queryParams.append("stage", "closed");
      } else {
        queryParams.append("stage", stage.toString());
      }
      queryParams.append("page_no", filters.stagePage?.toString() || "1");
      queryParams.append("limit", filters.limit?.toString() || "30");

      if (filters.start_date) {
        queryParams.append("start_date", start_date);
      }

      if (filters.end_date) {
        queryParams.append("end_date", end_date);
      }

      if (filters.owner_ids && filters.owner_ids.length > 0) {
        queryParams.append(
          "owner_ids",
          filters.owner_ids.map((item) => item.id).join(","),
        );
      }

      if (filters.team_ids && filters.team_ids.length > 0) {
        queryParams.append(
          "team_ids",
          filters.team_ids.map((item) => item.id).join(","),
        );
      }

      if (stage === PipelineStage.CLOSED) {
        const response = await getAPI(
          `/v5/sales-analytics/closed-leads-data?${queryParams.toString()}`,
        );
        return response.result;
      } else {
        const response = await getAPI(
          `/v5/sales-analytics/leads-data?${queryParams.toString()}`,
        );
        return response.result;
      }
    } catch (error) {
      throw error;
    }
  };

  getDealsPipeline = async (
    filters: SalesAnalyticsSelectedFilters,
    stage: number,
  ): Promise<PipelineData> => {
    try {
      const start_date = dayjs(filters.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const end_date = dayjs(filters.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const date = filters.end_date;

      const queryParams = new URLSearchParams();

      if (stage === PipelineStage.CLOSED) {
        queryParams.append("stage", "closed");
      } else {
        queryParams.append("stage", stage.toString());
      }
      queryParams.append("page_no", filters.stagePage?.toString() || "1");
      queryParams.append("limit", filters.limit?.toString() || "30");

      if (filters.start_date) {
        queryParams.append("start_date", start_date);
      }

      if (filters.end_date) {
        queryParams.append("end_date", end_date);
      }

      if (date) {
        queryParams.append("date", date);
      }

      if (filters.owner_ids && filters.owner_ids.length > 0) {
        queryParams.append(
          "owner_ids",
          filters.owner_ids.map((item) => item.id).join(","),
        );
      }

      if (filters.team_ids && filters.team_ids.length > 0) {
        queryParams.append(
          "team_ids",
          filters.team_ids.map((item) => item.id).join(","),
        );
      }

      if (stage === PipelineStage.CLOSED) {
        const response = await getAPI(
          `/v5/sales-analytics/closed-deals-data?${queryParams.toString()}`,
        );
        return response.result;
      } else {
        const response = await getAPI(
          `/v5/sales-analytics/deals-data?${queryParams.toString()}`,
        );
        return response.result;
      }
    } catch (error) {
      throw error;
    }
  };

  getIncentivesData = async (
    filters: SalesAnalyticsSelectedFilters,
  ): Promise<DailyIncentive[]> => {
    try {
      const start_date = dayjs(filters.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const end_date = dayjs(filters.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const date = filters.end_date;

      const queryParams = new URLSearchParams();

      if (start_date) queryParams.append("start_date", start_date);

      if (end_date) queryParams.append("end_date", end_date);

      if (date) {
        queryParams.append("date", date);
      }

      const response = await getAPI(
        `/v5/sales-analytics/incentives-data?${queryParams.toString()}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadTargetsTemplate = async (): Promise<APIBlobResponse> => {
    try {
      const response = await downloadCSV(
        `/v5/sales-analytics/download-targets-template`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  uploadTargets = async (file: File): Promise<APIResponse> => {
    try {
      const response = await uploadCSVAPI(
        `/v5/sales-analytics/upload-targets`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
}
